function trimTrailingSlash(value: string): string {
  return value.replace(/\/$/, "");
}

function extractHostname(value: string): string | null {
  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  try {
    const url = /^https?:\/\//i.test(normalized)
      ? new URL(normalized)
      : new URL(`https://${normalized}`);
    return url.hostname.trim().toLowerCase() || null;
  } catch {
    return null;
  }
}

function deriveSiblingOriginFromBrowser(
  currentPrefix: string,
  targetPrefix: string,
): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const hostname = window.location.hostname.trim().toLowerCase();
  const prefix = `${currentPrefix}.`;

  if (!hostname.startsWith(prefix)) {
    return null;
  }

  const rootDomain = hostname.slice(prefix.length);
  if (!rootDomain || !rootDomain.includes(".")) {
    return null;
  }

  return `${window.location.protocol}//${targetPrefix}.${rootDomain}`;
}

function resolveApiUrl(value: string): string {
  const trimmed = trimTrailingSlash(value);

  if (typeof window === "undefined" || !/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  try {
    const url = new URL(trimmed);
    if (url.origin === window.location.origin) {
      return trimTrailingSlash(url.pathname) || "/api";
    }
  } catch {
    // Fall through to the same-origin proxy path below.
  }

  return "/api";
}

function resolveMarketingAppUrl(value: string): string {
  const derived = deriveSiblingOriginFromBrowser("dashboard", "www");
  if (derived) {
    return derived;
  }

  const trimmed = trimTrailingSlash(value);
  if (!trimmed) {
    return "http://localhost:3002";
  }

  const configuredHost = extractHostname(trimmed);
  if (typeof window !== "undefined" && configuredHost) {
    const currentHost = window.location.hostname.trim().toLowerCase();
    if (currentHost === configuredHost) {
      return `${window.location.protocol}//${configuredHost}`;
    }
  }

  return trimmed;
}

export const API_URL = resolveApiUrl(import.meta.env.VITE_API_URL || "/api");
export const MARKETING_APP_URL = resolveMarketingAppUrl(
  import.meta.env.VITE_MARKETING_URL || "http://localhost:3002",
);

export function buildMarketingLoginUrl(
  returnTo?: string | null,
  options?: { loggedOut?: boolean },
): string {
  const url = new URL("/login", MARKETING_APP_URL);

  if (returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//")) {
    url.searchParams.set("returnTo", returnTo);
  }

  if (options?.loggedOut) {
    url.searchParams.set("loggedOut", "1");
  }

  return url.toString();
}

export const COMMON_LOCALE_SUGGESTIONS = [
  "en",
  "es",
  "fr",
  "de",
  "it",
  "pt",
  "nl",
  "ar",
] as const;

export type Locale = string;

export const LOCALE_CODE_PATTERN = /^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/i;

const LOCALE_META_BY_CODE: Record<
  string,
  { label: string; flag: string; nativeName: string }
> = {
  en: { label: "English", flag: "🇬🇧", nativeName: "English" },
  es: { label: "Spanish", flag: "🇪🇸", nativeName: "Español" },
  fr: { label: "French", flag: "🇫🇷", nativeName: "Français" },
  de: { label: "German", flag: "🇩🇪", nativeName: "Deutsch" },
  it: { label: "Italian", flag: "🇮🇹", nativeName: "Italiano" },
  pt: { label: "Portuguese", flag: "🇵🇹", nativeName: "Português" },
  nl: { label: "Dutch", flag: "🇳🇱", nativeName: "Nederlands" },
  ar: { label: "Arabic", flag: "🇸🇦", nativeName: "العربية" },
};

export function normalizeLocaleCode(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .replace(/_/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

export function isLocaleCode(
  value: string | null | undefined,
): value is string {
  const normalized = normalizeLocaleCode(value);
  return Boolean(normalized) && LOCALE_CODE_PATTERN.test(normalized);
}

export function mergeLocaleOptions(
  ...groups: Array<readonly (string | null | undefined)[] | null | undefined>
): string[] {
  const merged: string[] = [];

  for (const group of groups) {
    if (!group) {
      continue;
    }

    for (const entry of group) {
      const locale = normalizeLocaleCode(entry);
      if (isLocaleCode(locale)) {
        merged.push(locale);
      }
    }
  }

  return Array.from(new Set(merged));
}

export function getLocaleMeta(locale: string): {
  label: string;
  flag: string;
  nativeName: string;
} {
  const normalized = normalizeLocaleCode(locale);

  return (
    LOCALE_META_BY_CODE[normalized] ?? {
      label: normalized.toUpperCase(),
      flag: "🌐",
      nativeName: normalized.toUpperCase(),
    }
  );
}

// Backward-compatible alias while callers migrate to the helpers above.
export const LOCALES = COMMON_LOCALE_SUGGESTIONS;

export interface Page {
  id: string;
  slug: string;
  locale: Locale;
  title: string;
  puckData: Record<string, unknown>;
  published: boolean;
  seoTitle: string;
  seoDescription: string;
  targetKeywords: string;
  internalBrief: string;
  seoOgImage: string;
  seoCanonical: string;
  seoNoindex: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PageListItem {
  id: string;
  slug: string;
  locale: string;
  title: string;
  published: boolean;
  seoTitle: string;
  seoDescription: string;
  targetKeywords: string;
  internalBrief: string;
  seoOgImage: string;
  seoNoindex: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface PageVersion {
  id: string;
  savedBy: string;
  note: string;
  savedAt: string;
}
